import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { device, IconSvg, LimitCharacters, Main_Plain } from "monica-alexandria";
import NoImg from "../../../assets/NoImage.png";

const minioImageUrl = process.env.REACT_APP_MINIO_PROTOCOL +
    process.env.REACT_APP_MINIO_HOST +
    process.env.REACT_APP_MINIO_PORT +
    "/" +
    process.env.REACT_APP_MINIO_DAILYPROFIT_BUCKET;

const SidebarOptions = styled.div`
    padding-bottom: 4rem;

    .MainPlain {
        margin: 0 auto;
        display: ${p => p.sidebar ? "block" : "none"};
    }
`;

const SidebarOptionsList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const SidebarOptionsBox = styled.li`
    display: flex;
    align-items: center;
    justify-content: ${p => p.sidebar ? "left" : "center"};
    gap: 1rem;
    padding: 0.5rem;
    padding-left: ${p => p.sidebar ? "4rem" : "1rem"};
    cursor: pointer;
    background: ${p => p.active ? p.theme.low : "transparent"};

    img {
        width: 3rem;
        height: 3rem;
        border-radius: var(--smallRadius);
        object-fit: cover;
    }

    svg {
        width: 2.2rem;
        fill: ${p => p.active ? "var(--main)" : p.theme.color};
    }

    h5 {
        display: ${p => p.sidebar ? "block" : "none"};
        color: ${p => p.active ? "var(--main)" : p.theme.color};
    }

    &:hover {
        background: ${p => p.theme.mid};
    }

    @media ${device.sm} {
        padding-left: ${p => p.sidebar ? "2rem" : "1rem"};

        img {
            width: 3.5rem;
            height: 3.5rem;
        }

        svg {
            width: 3rem;
        }
    }
`;

const SidebarOptionsSep = styled.div`
    height: 0.2rem;
    width: ${p => p.sidebar ? "calc(100% - 8rem)" : "calc(100% - 4rem)"};
    margin: ${p => p.sidebar ? "1rem 4rem" : "1rem 2rem"};
    background: ${p => p.theme.low};

    @media ${device.sm} {
        width: calc(100% - 4rem);
        margin: 1rem 2rem;
    }
`;

const maxArchivedQuests = 5;

/**
 * Reads the visible CRM quest name from imported quest metadata.
 */
const read_quest_name = (quest) => {
    const questionnaireName = [
        quest?.questionnaires?.[0]?.answer,
        quest?.questionnaires?.[1]?.answer,
        quest?.Meta?.facebookPageName
    ].filter(Boolean).join(" ");
    const questName = quest?.name || questionnaireName || "";

    return LimitCharacters(questName, 55);
};

/**
 * Reads the best quest image available for the sidebar list.
 */
const read_quest_image_url = (quest) => {
    if (quest?.adInfo?.thumbnail && quest?.adInfo?.thumbnail !== "default.jpg") {
        return quest.adInfo.thumbnail;
    }

    if (quest?.adInfo?.visualElement) {
        return `${minioImageUrl}/${quest?.userId}/${quest.adInfo.visualElement}`;
    }

    return NoImg;
};

/**
 * Renders Niffler-specific sidebar actions inside the shared sidebar portal.
 */
export const Sidebar_Options = (props) => {
    const { t } = useTranslation();
    const [showAllArchived, setShowAllArchived] = useState(false);

    const quests = props?.quests || [];
    const archivedQuests = props?.archivedQuests || [];
    const hideAdminQuestNavigation = props?.hideAdminQuestNavigation === true;
    const visibleArchivedQuests = showAllArchived ? archivedQuests : archivedQuests.slice(0, maxArchivedQuests);
    const showArchivedToggle = archivedQuests.length > maxArchivedQuests;
    const archivedToggleText = {
        true: t("Show less"),
        false: t("View all")
    }[String(showAllArchived)];

    const generalOptions = [
        {
            key: "boards",
            icon: "Workspace",
            text: t("Boards"),
            active: props?.activeOption === "boards",
            onClick: props?.onBoardsClick
        },
        {
            key: "export",
            icon: "Download",
            text: t("Export data"),
            active: props?.activeOption === "export",
            onClick: props?.onExportClick
        }
    ].filter((option) => option.onClick);

    const growthOptions = [
        {
            key: "leads",
            icon: "User",
            text: t("Leads"),
            active: props?.activeOption === "leads",
            onClick: props?.onLeadsClick
        },
        {
            key: "brands",
            icon: "MarketList",
            text: t("Brands"),
            active: props?.activeOption === "brands",
            onClick: props?.onBrandsClick
        },
        {
            key: "sales",
            icon: "User",
            text: t("Sales"),
            active: props?.activeOption === "sales",
            onClick: props?.onSalesClick
        }
    ].filter((option) => option.onClick);

    return (
        <SidebarOptions sidebar={props?.sidebar}>
            
            <h6>{t("BOARDS")}</h6>
            <SidebarOptionsList>
                {growthOptions.map((option) => (
                    <SidebarOptionsBox
                        key={option.key}
                        sidebar={props?.sidebar}
                        active={option.active}
                        onClick={option.onClick}
                    >
                        <IconSvg Icon={option.icon} />
                        <h5>{option.text}</h5>
                    </SidebarOptionsBox>
                ))}
            </SidebarOptionsList>
            <SidebarOptionsSep sidebar={props?.sidebar} />
            {!hideAdminQuestNavigation && <>
                <h6>{t("GENERAL")}</h6>
                <SidebarOptionsList>
                    {generalOptions.map((option) => (
                        <SidebarOptionsBox
                            key={option.key}
                            sidebar={props?.sidebar}
                            active={option.active}
                            onClick={option.onClick}
                        >
                            <IconSvg Icon={option.icon} />
                            <h5>{option.text}</h5>
                        </SidebarOptionsBox>
                    ))}
                </SidebarOptionsList>

                <SidebarOptionsSep sidebar={props?.sidebar} />
                <h6>{t("Active Quests")}</h6>
                <SidebarOptionsList>
                    {quests.map((quest, index) => (
                        <SidebarOptionsBox
                            key={quest?._id || index}
                            sidebar={props?.sidebar}
                            active={String(props?.activeQuestId) === String(quest?._id)}
                            onClick={() => props?.onQuestClick(quest?._id)}
                        >
                            <img src={read_quest_image_url(quest)} alt={read_quest_name(quest)} />
                            <h5>{read_quest_name(quest)}</h5>
                        </SidebarOptionsBox>
                    ))}
                </SidebarOptionsList>

                <SidebarOptionsSep sidebar={props?.sidebar} />
                <h6>{t("Archived Quests")}</h6>
                <SidebarOptionsList>
                    {visibleArchivedQuests.map((quest, index) => (
                        <SidebarOptionsBox
                            key={quest?._id || `archived-${index}`}
                            sidebar={props?.sidebar}
                            active={String(props?.activeQuestId) === String(quest?._id)}
                            onClick={() => props?.onQuestClick(quest?._id)}
                        >
                            <img src={read_quest_image_url(quest)} alt={read_quest_name(quest)} />
                            <h5>{read_quest_name(quest)}</h5>
                        </SidebarOptionsBox>
                    ))}
                </SidebarOptionsList>

                {showArchivedToggle && (
                    <Main_Plain
                        text={archivedToggleText}
                        onClick={() => setShowAllArchived(previousValue => !previousValue)}
                        size="small"
                    />
                )}
            </>}
        </SidebarOptions>
    );
};
